/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useState } from "react";
import { TextInput, Select } from "flowbite-react";
import { HiOutlinePlus } from "react-icons/hi";
import { StandardForm, EntityFormDialog } from "@components";
import { IWorkspaceAccessBase, IWorkspaceAccess } from "../../types";
import { useTranslation } from "react-i18next";
import { WorkspaceAccess } from "../../models";
import { useUserSession } from "../../context/UserContext";
import { useWorkspace } from "../../context/WorkspaceContext";

export interface UserModalProp {
  data: IWorkspaceAccess;
  isShowing: boolean;
  onConfirm: (data: IWorkspaceAccessBase) => void;
  onCancel: () => void;
}

export const UserForm: FC<UserModalProp> = function (props: UserModalProp) {
  const { t } = useTranslation(["common", "user", "workspace"]);
  const { userSession } = useUserSession();
  const { activeWorkspace } = useWorkspace();
  const [isNew] = useState(WorkspaceAccess.isNew(props.data));

  const handleConfirm = (data: IWorkspaceAccessBase) => {
    // If the email matches the current user's email, use their current role
    if (data.email === userSession?.user.email && activeWorkspace) {
      data.role = activeWorkspace.role;
    }
    props.onConfirm(data);
  };

  return (
    <StandardForm
      showHeader={false}
      description={t("modals:descriptions.new_user")}
      fields={[
        {
          id: "email",
          label: t("common:email"),
          input: (
            <TextInput
              id="email"
              key="email"
              name="email"
              placeholder={t("common:email")}
              defaultValue={props.data.email}
              type="text"
              required={true}
            />
          ),
        },
        {
          id: "role",
          label: t("user:role"),
          input: (
            <Select
              defaultValue={props.data.role}
              id="role"
              key="role"
              name="role"
            >
              <option value="ReadOnly">{t("workspace:roles.ReadOnly")}</option>
              <option value="OrderDraft">
                {t("workspace:roles.OrderDraft")}
              </option>
              <option value="Administrator">
                {t("workspace:roles.Administrator")}
              </option>
              <option value="Owner">{t("workspace:roles.Owner")}</option>
            </Select>
          ),
        },
      ]}
      onConfirm={handleConfirm}
      onCancel={props.onCancel}
      buttonConfirmText={t("modals:button_texts.user")}
      buttonConfirmPrependIcon={
        isNew ? <HiOutlinePlus className="mr-2 h-4 w-4" /> : null
      }
      buttonCloseText={t("common:close")}
    />
  );
};

export const UserModal: FC<UserModalProp> = function (props: UserModalProp) {
  const { t } = useTranslation(["common", "user", "modals"]);

  return (
    <EntityFormDialog
      title={
        WorkspaceAccess.isNew(props.data)
          ? t("user:new_user")
          : t("user:edit_user")
      }
      show={props.isShowing}
      onCancel={props.onCancel}
    >
      <UserForm {...props} />
    </EntityFormDialog>
  );
};
